import React, { Component } from 'react';
import { PropTypes } from 'prop-types';
import { Loader } from '@citrus/loader';
import { Layout, Header, Row, Col } from '@citrus/layout';
import { PrimaryButton, SecondaryButton, BorderlessButton } from '@citrus/button';
import ConfirmationModal from '../Common/Components/confirmation/confirmationModal';
import FlashMessage from '../Common/Components/flashMessage/flashMessage.container';
import { Banner } from '@citrus/banner';
import { getSurveyCategoryTypes } from './formEditor.constants';
import { TalentFormFormTypes } from '../formViewer/formViewer.constants';
import CitrusFormEditor from "../Common/Components/citrusFormEditor/citrusFormEditor";
import { isNullOrEmpty } from '@pcty/utils';
import * as VideoApiService from '../Common/Services/VideoApiService';
import moment from 'moment';


const autoSaveFrequencyMs = 120000;  // 2-min autosave (for now)

class FormEditor extends Component {

  constructor(props) {
    super(props);

    this.autoSaveIntervalId = undefined;
  }

  UNSAFE_componentWillMount() {
    const companyId = this.props.params.companyId;
    const employeeId = this.props.params.employeeId;
    const formId = this.props.params.formId;
    const formTypeId = parseInt(this.props.params.formTypeId || '0');
    const copy = this.props.params.action == "copy";
    const share = this.props.params.action == "share";
    const view = this.props.params.action == "view";
    const createdByName = this.props.params.createdByName;
    const wizardFormName = this.props.params.wizardFormName;
    const wizardFormCategoryType = this.props.params.wizardFormCategoryType;
    const questionBanksEnabled = this.isOptionEnabled("questionBanksEnabled");

    if (formId && (parseInt(formId) > 0 || formId.length > 12)) {
      this.props.loadFormTemplate(companyId, employeeId, formId, copy, share, view, createdByName, wizardFormName, wizardFormCategoryType);
    }
    else {
      this.props.createFormTemplate(companyId, employeeId, formTypeId, createdByName, wizardFormName, wizardFormCategoryType);
    }

    if (questionBanksEnabled) {
      this.props.loadQuestionBank(formTypeId);
    }
  }

  componentDidMount() {
    this.autoSaveIntervalId = setInterval(() => this.saveAsDraftIfDirty(), autoSaveFrequencyMs);
  }

  componentWillUnmount() {
    // MUST clearInterval() or the interval/auto-save thread will keep going and going and going!
    clearInterval(this.autoSaveIntervalId);
  }

  saveTemplateConfiguration() {
    let theForm = {...this.props.form};

    const moduleId = this.getOptionValue("moduleId");
    if (moduleId) {
      theForm.moduleId = moduleId;
    }
    
    this.props.saveFormTemplate(theForm, false);
  }

  saveAsDraftIfDirty() {
    let theForm = {...this.props.form};
    const moduleId = this.getOptionValue("moduleId");
    if (moduleId) {
      theForm.moduleId = moduleId;
    }

    let isFormValid = this.props.isFormValid;

    if (this.props.isFormModified && this.props.formTypeId === TalentFormFormTypes.Surveys &&
      !isFormValid && !isNullOrEmpty(theForm.configuration) &&
      (isNullOrEmpty(theForm.templateName) || isNullOrEmpty(theForm.formCategoryTypeId))) {

      theForm.templateName = theForm.templateName || 'Draft Survey ' + moment().format('YYYY-MM-DD hh:mm:ss');
      theForm.formCategoryTypeId = theForm.formCategoryTypeId || getSurveyCategoryTypes()[8].id;
      isFormValid = true;
    }

    if (isFormValid && this.props.isFormModified && !this.props.isSavingTemplate) {
      this.props.saveFormTemplate(theForm, true);
    }
  }

  renderAutoSaveText() {
    return (this.props.lastAutoSave &&
      <Row>
        <Col className="pcty-type-rightalign" style={{ marginBottom: '15px' }}>
          <em>{"Last saved"}: <span>{moment(this.props.lastAutoSave).format('M/D/YYYY h:mm:ss a')}</span></em>
        </Col>
      </Row>
    );
  }

  isOptionEnabled(option) {
    let enabled = (this.props.options && this.props.options[option] === true) ? true : false;
    return enabled;
  }

  getOptionValue(option) {
    let value = (this.props.options && this.props.options[option]) ? this.props.options[option] : undefined;
    return value;
  }

  handleBackClick() {
    var evt = new CustomEvent('PctyTalentFormEditorGoBack', { detail: this.props.params });
    window.dispatchEvent(evt);
  }

  cancelChanges() {
    var evt = new CustomEvent('PctyTalentFormEditorCancelled', { detail: this.props.form });
    window.dispatchEvent(evt);
  }

  renderCancelConfirmationModal() {
    return (this.props.isFormModified &&
      <ConfirmationModal
        headerText="Unsaved Changes"
        body="You have changes that will be lost if you leave without saving."
        confirmText="Discard Changes"
        dismissText="Cancel"
        onConfirmCallback={() => this.cancelChanges()}
      />
    );
  }

  handleCancelChangesClick = () => {
    if (this.props.isFormModified)
      this.props.handleCancelClick();
    else
      this.cancelChanges();
  }

  raiseActionButtonEvent() {
    var evt = new CustomEvent('PctyTalentFormEditorViewActionButtonClick', { detail: this.props.params });
    window.dispatchEvent(evt);
  }

  onGetViewVideoToken(companyId, videoId) {
    if (!isNullOrEmpty(videoId)) {
     return VideoApiService.getSingleViewToken(companyId, videoId, false);
    }
  };

  onGetSingleManageVideoToken(companyId, employeeId, videoId) {
    if (!isNullOrEmpty(videoId)) {
     return VideoApiService.getSingleManageToken(companyId, employeeId, videoId);
    }
  };

  render() {
    const companyId = this.props.params.companyId;
    const employeeId = this.props.params.employeeId;
    let preventEditIfLaunched = this.isOptionEnabled('preventEditIfLaunched');
    let useSurveyLabels = this.isOptionEnabled("useSurveyLabels") || this.props.formTypeId === TalentFormFormTypes.Surveys;
    let showFormCategories = this.isOptionEnabled('showFormCategories') || this.props.formTypeId === TalentFormFormTypes.Surveys;
    let formCategoryTypes = this.props.formTypeId === TalentFormFormTypes.Surveys ? getSurveyCategoryTypes() : [];
    let isCategoryRequired = (this.props.formTypeId === TalentFormFormTypes.Surveys);
    let isReadOnly = (this.props.form && (this.props.form.isDefaultTemplate || (preventEditIfLaunched && this.props.form.launchCount > 0)));
    let viewActionButtonCaption = this.getOptionValue("viewActionButton");
    let viewActionButtonEnabled = this.isOptionEnabled("viewActionButtonEnabled");
    let premiumVideoEnabled = this.isOptionEnabled("allowVideoFeature");
    let questionCommentsEnabled = this.isOptionEnabled("questionCommentsEnabled");
    let questionBanksEnabled = this.isOptionEnabled("questionBanksEnabled");

    const renderActionArea = () => { return <React.Fragment>
      { this.props.isViewAction ?
        (
          viewActionButtonCaption ?
          <SecondaryButton
            automation-id={`template-launch-action`}
            onClick={() => this.raiseActionButtonEvent()}
            disabled={!viewActionButtonEnabled}>{viewActionButtonCaption}</SecondaryButton>
          :
          null
        )
        :
          <React.Fragment>
            <BorderlessButton key="header-cancel"
              onClick={() => this.handleCancelChangesClick()}
              disabled={this.props.isSavingTemplate}>Cancel</BorderlessButton>
            <PrimaryButton  key="header-save"
              onClick={() => this.saveTemplateConfiguration(this.props.form)}
              disabled={!this.props.isFormValid || this.props.isSavingTemplate}>Save</PrimaryButton>
          </React.Fragment>

      }
      </React.Fragment>
    };

    const renderHeader = () => {
      return <React.Fragment>
      { this.props.isViewAction ?
        <Header automationId="talent-forms-v2-header" renderActionArea={renderActionArea} title={this.props.form.templateName} onBack={() => this.handleBackClick()} />
        :
        <Header automationId="talent-forms-v2-header" renderActionArea={renderActionArea} title={useSurveyLabels ? 'Create Survey' : 'Create Form'} onBack={() => this.handleBackClick()} />
      }
      </React.Fragment>
    }

    //if an SBUser is skipping the templates, hide the category and set it to Other...
    if (this.props.options && this.props.options.bypassTemplateSelection) {
      showFormCategories = false;
      isCategoryRequired = false;
      this.props.form.formCategoryTypeId = '9';
    }

    const renderPreviewBanner = () => {
      return (!viewActionButtonEnabled && 
        <Banner
          id="preview-mode-info-banner"
          automationId="preview-mode-info-banner"
          type="information">
          <p>You are viewing this survey in the preview mode.</p>
        </Banner>
      )
    }

    return (
      <React.Fragment>
        <Loader loading={this.props.isLoading || this.props.formTypeId === 0} global={true} />
        <FlashMessage />
        {this.renderCancelConfirmationModal()}
        {!this.props.isLoading && this.props.formTypeId > 0 &&
          <Layout renderHeader={renderHeader}>
            {renderPreviewBanner()}
            <CitrusFormEditor
              formLabel={useSurveyLabels ? 'survey' : 'form'}
              configuration={this.props.form.configuration}
              showCategories={showFormCategories}
              formCategories={formCategoryTypes}
              selectedFormCategoryId={this.props.form.formCategoryTypeId}
              categoryRequired={isCategoryRequired}
              formName={this.props.form.templateName}
              editRestricted={isReadOnly || this.props.isViewAction}
              onConfigurationModified={(configuration) => this.props.formEditorConfigurationChanged(configuration)}
              onNameChange={(value) => this.props.formEditorFieldChanged("templateName", value)}
              onFormCategoryTypeChange={(value) => this.props.formEditorFieldChanged("formCategoryTypeId", value)}
              onFormQuestionsValidChanged={(value) => this.props.formEditorQuestionsValidChanged(value)}
              premiumVideoEnabled={premiumVideoEnabled}
              onGetViewVideoToken={(videoId) => this.onGetViewVideoToken(companyId, videoId)}
              onGetSingleManageVideoToken={(videoId) => this.onGetSingleManageVideoToken(companyId, employeeId, videoId)}              
              videoStudioEnabled={this.props.videoConfiguration ? this.props.videoConfiguration.videoStudioEnabled : false}
              questionCommentsEnabled={questionCommentsEnabled}
              questionBanksEnabled={questionBanksEnabled}
              questionBankQuestions={this.props.questionBankQuestions}
            />
            {this.renderAutoSaveText()}
          </Layout>
        }
      </React.Fragment>
    );
  }

}


FormEditor.propTypes = {
  params: PropTypes.object.isRequired,
  options: PropTypes.object,
  isLoading: PropTypes.bool.isRequired,
  createFormTemplate: PropTypes.func.isRequired,
  loadFormTemplate: PropTypes.func.isRequired,
  form: PropTypes.object.isRequired,
  isFormValid: PropTypes.bool.isRequired,
  isFormModified: PropTypes.bool.isRequired,
  isSavingTemplate: PropTypes.bool.isRequired,
  isCopyAction: PropTypes.bool.isRequired,
  isViewAction: PropTypes.bool.isRequired,
  handleCancelClick: PropTypes.func.isRequired,
  formTypeId: PropTypes.number.isRequired,
  formEditorFieldChanged: PropTypes.func.isRequired,
  formEditorQuestionsValidChanged: PropTypes.func.isRequired,
  formEditorConfigurationChanged: PropTypes.func.isRequired,
  saveFormTemplate: PropTypes.func.isRequired,
  lastAutoSave: PropTypes.object,
  videoConfiguration: PropTypes.object.isRequired,
  loadQuestionBank: PropTypes.func.isRequired,
  questionBankQuestions: PropTypes.arrayOf(PropTypes.any).isRequired,
};


FormEditor.contextTypes = {
  router: PropTypes.object
};

export default FormEditor;
